import React, { memo, useMemo, useCallback } from 'react';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { usePageVisibility } from '@atlassian/jira-use-page-visibilty-controller/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { RealtimeEvent } from '../../common/types/events.tsx';
import { SOURCE_SUBSCRIPTIONS } from '../../common/constants/sources.tsx';
import {
	BOARD_SUPPORTED_EVENTS,
	SUBSCRIPTION_SUPPORTED_EVENTS,
} from '../../common/constants/events.tsx';
import { analyticEvents, analyticsComponent } from '../../common/constants/analyticEvents.tsx';

import { IssueSubscription } from './subscriptions/IssueSubscription.tsx';
import { BoardIssueSubscription } from './subscriptions/BoardIssueSubscription.tsx';

const getProjectIdsToSubscribe = (channels: string[]) =>
	channels
		?.filter((projectAri) => JiraProjectAri.check(projectAri))
		?.map((projectAri) => JiraProjectAri.parse(projectAri))
		?.map((data) => data.projectId)
		?.filter(Boolean) ?? [];

const getIssueEventToSubscribe = (events: string[]) =>
	events?.filter(Boolean)?.filter((event) => SUBSCRIPTION_SUPPORTED_EVENTS.has(event)) ?? [];

const getBoardEventToSubscribe = (events: string[]) =>
	events?.filter(Boolean)?.filter((event) => BOARD_SUPPORTED_EVENTS.has(event)) ?? [];

type Props = {
	appId: string;
	channels: string[];
	events: string[];
	onJoin: (channels: string[]) => void;
	onReceive: (event: RealtimeEvent) => void;
	onLeave: (channels: string[]) => void;
};

export const RealtimeSubscriptions: React.FC<Props> = memo((props) => {
	const { appId, channels, events, onJoin, onReceive, onLeave } = props;

	const atlassianAccountId = useAccountId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isVisible = usePageVisibility();

	const projectIds = useMemo(() => getProjectIdsToSubscribe(channels), [channels]);
	const issueEvents = useMemo(() => getIssueEventToSubscribe(events), [events]);
	const boardEvents = useMemo(() => getBoardEventToSubscribe(events), [events]);

	const handleOnJoin = useCallback(() => {
		onJoin(channels);
	}, [onJoin, channels]);

	const handleOnReceive = useCallback(
		(event: RealtimeEvent) => {
			const {
				type,
				payload: { atlassianId },
			} = event;

			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				`${analyticsComponent} ${analyticEvents.EVENT_RECEIVED}`,
				{
					source: SOURCE_SUBSCRIPTIONS,
					eventType: type,
					appId,
					channels,
					isTriggeredBySameUser: atlassianId === atlassianAccountId,
				},
			);

			onReceive(event);
		},
		[appId, atlassianAccountId, channels, createAnalyticsEvent, onReceive],
	);

	const handleOnLeave = useCallback(() => {
		onLeave(channels);
	}, [channels, onLeave]);

	const handleOnError = useCallback(
		(error: Error) => {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				`${analyticsComponent} ${analyticEvents.FAILURE}`,
				{
					source: SOURCE_SUBSCRIPTIONS,
					appId,
					channels,
					events: issueEvents,
					error: error.message,
				},
			);
		},
		[channels, appId, issueEvents, createAnalyticsEvent],
	);

	const shouldRenderIssueSubscription = projectIds?.length > 0 && issueEvents?.length > 0;
	const shouldRenderBoardIssueSubscription = projectIds?.length > 0 && boardEvents?.length > 0;

	// Do not render the component on server-side
	// rendering to avoid hydration issues
	if (__SERVER__ && fg('jirt-convert-class-to-functional-component')) {
		return null;
	}

	// Do not render the component when the page or tab is not visible
	// this will force the subscription components to unsubscribe
	// and avoid unnecessary data transfer when the user is not visualizing
	// the page. The subscription will be re-established when the page
	// or tab is visible again.
	if (!isVisible) {
		return null;
	}

	return (
		<>
			{shouldRenderIssueSubscription && (
				<IssueSubscription
					appId={appId}
					projectIds={projectIds}
					events={issueEvents}
					onJoin={handleOnJoin}
					onReceive={handleOnReceive}
					onLeave={handleOnLeave}
					onError={handleOnError}
				/>
			)}
			{shouldRenderBoardIssueSubscription && (
				<BoardIssueSubscription
					appId={appId}
					projectIds={projectIds}
					events={boardEvents}
					onJoin={handleOnJoin}
					onReceive={handleOnReceive}
					onLeave={handleOnLeave}
					onError={handleOnError}
				/>
			)}
		</>
	);
});
